import type { SlashCommand } from "@codenexus/react";

import { resolveTaxonomy, type TaxonomyResolution } from "@/help/resolver";

// Global Help concierge skill id (KAN-1358). Fired by the header Help button and
// also used by the router's help-intent fast path.
export const GLOBAL_HELP_SKILL = "global-product-studio-help";

// `/help` goes through the normal send path so its context includes the current
// module and taxonomy. It intentionally has no host-navigation side effects.
export const HELP_SLASH_COMMANDS: SlashCommand[] = [
    {
        name: "help",
        description: "Find your way around Product Studio",
        mode: "skill",
        execute: ctx => ctx.send("help")
    }
];

// Canonical Global Help module vocabulary. The standalone new-product screen
// remains currentPage `new-product`, but belongs to Product Hub for help routing.
export function deriveCurrentModule(currentView: string | undefined): string {
    switch (currentView) {
        case "projects":
        case "new-product":
            return "product-hub";
        case "features":
            return "feature-studio";
        case "editor":
            return "work-product-editor";
        case "integrations":
            return "integrations-console";
        case "uploads":
            return "file-management";
        default:
            return "any-workspace";
    }
}

/** Views whose FDE tools must retain the currently selected product. */
export function isProductScopedChatView(currentView: string): boolean {
    return currentView === "features" || currentView === "editor" || currentView === "mesh-strength";
}

/** Deep-link navigation already has an active FDE turn explaining the result. */
export function shouldFireFeatureStudioWelcome(context: unknown): boolean {
    return !(
        typeof context === "object" &&
        context !== null &&
        "suppressPageWelcome" in context &&
        context.suppressPageWelcome === true
    );
}

// Canonical route per view. The SPA only rewrites window.location.pathname on
// new-product transitions, so the raw pathname does NOT identify the view —
// and return-to-origin's same-location guard compares route FIRST. Pushing a
// stale shared pathname as currentRoute made "projects" and "uploads" compare
// equal on route, so the captured origin read as "already there" (no-origin).
// The pushed route must therefore be derived from the view itself.
const VIEW_ROUTES: Record<string, string> = {
    projects: "/products",
    "new-product": "/products/new",
    "refresh-product": "/products/refresh",
    features: "/feature-studio",
    editor: "/editor",
    uploads: "/uploads",
    integrations: "/integrations",
    profile: "/profile",
    admin: "/admin"
};

export function deriveHostLocationContext(currentView: string, currentRoute: string | undefined) {
    return {
        currentPage: currentView,
        currentRoute: VIEW_ROUTES[currentView] ?? currentRoute,
        currentModule: deriveCurrentModule(currentView)
    };
}

export interface AmbientProductContext {
    projectId: number | null;
    projectName: string | null;
}

interface ProductIdentitySource {
    id?: unknown;
    project_id?: unknown;
    projectid?: unknown;
    name?: unknown;
    project_name?: unknown;
}

function positiveProjectId(...candidates: unknown[]): number | null {
    for (const candidate of candidates) {
        const value = typeof candidate === "number" ? candidate : Number(candidate);
        if (Number.isSafeInteger(value) && value > 0) return value;
    }
    return null;
}

/** Normalizes the two project shapes used by Product Hub/Redux. In particular,
 * Product Hub selections carry the canonical id in `id`, while older screens
 * expose `project_id`; treating the name alone as live context would otherwise
 * hide the persisted id and make Context Mesh appear unscoped. */
export function resolveActiveProductContext(
    currentProject: ProductIdentitySource | null | undefined,
    selectedProject: ProductIdentitySource | null | undefined
): AmbientProductContext | null {
    const projectId = positiveProjectId(
        currentProject?.project_id,
        currentProject?.projectid,
        currentProject?.id,
        selectedProject?.project_id,
        selectedProject?.projectid,
        selectedProject?.id
    );
    const rawName =
        currentProject?.project_name ??
        currentProject?.name ??
        selectedProject?.project_name ??
        selectedProject?.name;
    const projectName = typeof rawName === "string" && rawName.trim() ? rawName.trim() : null;
    return projectId !== null || projectName !== null ? { projectId, projectName } : null;
}

export interface AmbientHierarchyContext {
    hierarchyLevel: "product" | "feature" | "user_story";
    hierarchyName: string;
    projectId?: string | number | null;
    projectName?: string | null;
    featureId?: string | number | null;
    featureName?: string | null;
    userStoryId?: string | null;
    userStoryName?: string | null;
}

export type ScopedWorkProductActivationResult =
    | { status: "activated" }
    | { status: "not_found" }
    | { status: "disabled" };

/** Activates an exact work-product card discovered through list_ui_elements.
 * Comparing attributes directly avoids rebuilding or CSS-escaping scoped ids. */
export function activateScopedWorkProductElement(
    elementId: string,
    root: ParentNode = document
): ScopedWorkProductActivationResult {
    const target = Array.from(root.querySelectorAll<HTMLElement>("[data-ui-bridge]"))
        .find(element => element.getAttribute("data-ui-bridge") === elementId);
    if (!target) return { status: "not_found" };
    if (target.hasAttribute("disabled") || target.getAttribute("aria-disabled") === "true") {
        return { status: "disabled" };
    }
    target.click();
    return { status: "activated" };
}

/** Ambient per-message context payload. Product identity is pushed with every
 *  message so agent skills don't depend on remembering to call
 *  get_current_product; nulls are sent explicitly on non-product screens so a
 *  stale product from a prior view can never leak through. */
export function buildViewContextPayload(
    currentView: string,
    currentRoute: string | undefined,
    activeProduct: AmbientProductContext | null | undefined,
    editorFields: Record<string, unknown>,
    activeHierarchy?: AmbientHierarchyContext | null
) {
    return {
        ...deriveHostLocationContext(currentView, currentRoute),
        activeProductId: activeProduct?.projectId ?? null,
        activeProductName: activeProduct?.projectName ?? null,
        // Send an explicit null outside product-scoped views so a prior feature
        // or story can never leak into a later FDE turn.
        activeHierarchy: activeHierarchy ?? null,
        ...editorFields
    };
}

export interface ChatTaxonomyResolution {
    matches: Array<{
        id: string;
        ui: string;
        score: number;
        matched_alias?: string;
    }>;
    version?: string;
}

// Core transports matched aliases in snake_case. Keep that protocol adaptation
// at the host boundary instead of leaking it into the application resolver.
export function adaptTaxonomyForChat({ matches, version }: TaxonomyResolution): ChatTaxonomyResolution {
    return {
        matches: matches.map(match => ({
            id: match.id,
            ui: match.ui,
            score: match.score,
            matched_alias: match.matchedAlias
        })),
        version
    };
}

export function resolveTaxonomyForChat(message: string): ChatTaxonomyResolution {
    return adaptTaxonomyForChat(resolveTaxonomy(message));
}

export interface SectionNavigationTarget {
    sectionId: string;
    sectionTitle: string;
    index: number;
}

export type SectionNavigationPlan =
    | { type: "confirm"; target: SectionNavigationTarget }
    | { type: "switch"; target: SectionNavigationTarget; restoreEditorView: boolean };

/**
 * Decides what a chip/agent-driven "navigate to section" request
 * (`handleNavigateToSection`) should do, given whether the currently-mounted
 * editor has unresolved staged edits and whether the editor is even the
 * active view right now.
 *
 * - Pending edits on the currently-mounted editor always win: surface the
 *   existing discard-confirmation flow, regardless of view. (In practice
 *   `hasPendingEdits()` can only be true when the editor is mounted, i.e.
 *   `currentView === "editor"` — there's no mounted TipTap instance to hold
 *   staged marks otherwise — but the check doesn't assume that.)
 * - Otherwise, switch directly. `restoreEditorView` tells the caller whether
 *   it must also flip the host view back to the editor before applying the
 *   tab switch — required whenever the editor isn't the active view (Feature
 *   Studio, Uploads, etc.), since a chip's entire point is to work from
 *   there. On-editor clicks are unaffected: `restoreEditorView` is false and
 *   the caller's existing behavior (switch tab, nothing else) is preserved.
 */
export function planSectionNavigation(
    target: SectionNavigationTarget,
    { currentView, hasPendingEdits }: { currentView: string; hasPendingEdits: boolean }
): SectionNavigationPlan {
    if (hasPendingEdits) {
        return { type: "confirm", target };
    }
    return { type: "switch", target, restoreEditorView: currentView !== "editor" };
}
